/**
 * Drag and drop id namespaces for the Todos workspace.
 *
 * The Todos page drives a single page-level `DndContext` that has to mediate
 * between several distinct interactions:
 *
 *  - Whole-card drags                     `ws-drag:${kind}:${id}`
 *  - Mini-chip drags inside ScheduleDropZones `ws-drag-zone:${kind}:${id}`
 *  - Action item drags (cross-card)        `ws-act:${kind}:${parentId}:${itemId}`
 *  - Bullets outline drags                 `ws-bullet-drag:…` (see BulletsCanvas)
 *
 *  Drop targets:
 *  - Schedule lane chips                   `ws-list-lane:${lane}`
 *  - Unschedule chip                       `ws-unschedule`
 *  - Project switcher pills                `ws-project-pill:${id|__inbox__}`
 *  - Project Kanban columns                `ws-proj-col:${projectKey}:${status}`
 *  - Bullets indent / lane drops           `ws-bullet-lane:` / `ws-bullet-indent:` (see BulletsCanvas)
 *
 * This module only owns the **action-item** namespace because that's the
 * one that needed to be added when we unified TaskCard's action-item drags
 * into the page-level DndContext (so an item can be dragged out of its
 * parent card onto a project column or schedule chip).
 */

export type ActionItemParentKind = 'task' | 'focus';

const ACTION_ITEM_DRAG_PREFIX = 'ws-act:';

/** Build a stable drag id for an action item that's globally unique on the page. */
export function makeActionItemDragId(
  parentKind: ActionItemParentKind,
  parentId: string,
  actionItemId: string,
): string {
  return `${ACTION_ITEM_DRAG_PREFIX}${parentKind}:${parentId}:${actionItemId}`;
}

export interface ParsedActionItemDragId {
  parentKind: ActionItemParentKind;
  parentId: string;
  actionItemId: string;
}

/** Returns null when `raw` doesn't belong to the action-item drag namespace. */
export function parseActionItemDragId(raw: string): ParsedActionItemDragId | null {
  if (!raw.startsWith(ACTION_ITEM_DRAG_PREFIX)) return null;
  const rest = raw.slice(ACTION_ITEM_DRAG_PREFIX.length);
  const parts = rest.split(':');
  if (parts.length < 3) return null;
  const [kindRaw, parentId, ...tail] = parts;
  if (kindRaw !== 'task' && kindRaw !== 'focus') return null;
  return {
    parentKind: kindRaw,
    parentId,
    actionItemId: tail.join(':'),
  };
}

/** True for any id under `ws-act:`. Used for quick branching in drag-end handlers. */
export function isActionItemDragId(raw: string): boolean {
  return raw.startsWith(ACTION_ITEM_DRAG_PREFIX);
}
